/**
 * The shapes Mira's tasks agree on across packages.
 *
 * `apps/api` asks a model for them and validates what comes back; `apps/counter` builds the request
 * and decides the answer field by field. Neither imports the other, so anything they both name has
 * to be named here or it drifts, which is what happened to the prescription keys below.
 *
 * `AI-010` reads a slip and `AI-001` has a person decide each value, which puts the same four
 * per-eye keys in two places that never see each other: the prompt schema in `apps/api` that asks
 * the model for them, and the acceptance screen in `apps/counter` that validates and displays what
 * came back. They were written separately and disagreed. The screen looked for `sphere` and
 * `cylinder` holding numbers; the schema asks for `sph` and `cyl` holding strings, so a proposed
 * `-3.30` was accepted exactly as it stood (the 0.25 gate never saw it), and every axis and add was
 * permanently invalid because a string is not a number. A prescription that cannot be carried at
 * all is the good half of that; a sphere off the 0.25 grid reaching the grid is the bad half.
 *
 * So the keys live here, in the one package both sides already depend on, and each side reads them
 * rather than naming them again.
 *
 * **Values are strings, as written on the slip.** `-2.00` and `-2.0` are the same number and not
 * the same reading, and a JSON float is the first step to a rounding error nobody can find. The
 * 0.25 grid is checked where the string is parsed, which is `AI-001`'s acceptance step.
 */
/**
 * ## `rx_ocr` (`AI-010`): what a prescription read returns
 *
 * Right eye then left, as a slip lists them.
 */
export const RX_READ_EYES = ['od', 'os'];
/** The four values a distance prescription carries per eye, in the order the schema lists them. */
export const RX_READ_COLUMNS = ['sph', 'cyl', 'axis', 'add'];
/**
 * The `RxCapture` grid column each read column fills.
 *
 * The slip says `sph`, the grid says `sphere`, and both names are right where they are: one is the
 * abbreviation printed on a prescription, the other is the column of a record. This is the mapping,
 * so neither side has to guess the other's spelling.
 */
export const RX_READ_TO_GRID = {
    sph: 'sphere',
    cyl: 'cylinder',
    axis: 'axis',
    add: 'add',
};
/** `axis` is whole degrees (Appendix I); the other three are powers on the 0.25 grid. */
export const isRxReadAxis = (column) => column === 'axis';
// -----------------------------------------------------------------------------------------------
// AI-015: what a message draft is asked for
// -----------------------------------------------------------------------------------------------
/**
 * The four occasions `AI-015` drafts for, and the three channels it writes for.
 *
 * Both are part of the request and not of the answer: `message_draft` has no use for a free-text
 * question, and a payload that carries one and not these two is a payload no model can serve. The
 * counter's form is built from these lists and the provider validates against them, so a fourth
 * channel is one edit and not three that have to agree.
 *
 * The channels are what a draft is *written for*, which is not the same as what the outbox can
 * send: `V1_MESSAGE_CHANNELS` is email and in-app, and a WhatsApp draft in v1 is text a person
 * copies into WhatsApp themselves (`PK-021` makes it the channel shops actually use).
 */
export const MIRA_DRAFT_OCCASIONS = ['order_ready', 'balance_due', 'recall', 'apology'];
export const MIRA_DRAFT_CHANNELS = ['whatsapp', 'sms', 'email'];
// -----------------------------------------------------------------------------------------------
// AI-003 and AI-004: what an image-bearing request may carry
// -----------------------------------------------------------------------------------------------
/**
 * ## The image payload contract (`AI-010`, `AI-011`, `AI-012`)
 *
 * `apps/api` validates against these and `apps/counter` builds to them, which is the same reason
 * the prescription keys above live here.
 *
 * **The two numbers below are the product's and not the spec's.** The PRD defines no image payload
 * at all; `PROGRESS.md` 182 records that as the reason the three vision tasks shipped unreachable.
 * `anthropic-provider.ts` enforces Anthropic's own documented ceilings (8 images, about 12M base64
 * characters, 10 MB an image), which are limits on what their API accepts and say nothing about
 * what a shop can send. These are deliberately tighter, and the constraint that set them is a cheap
 * Android tablet on a Pakistani mobile connection: four photographs at roughly 1.5 MB each is about
 * 8 MB of base64 on the wire, which is already a slow upload on a 3G link and is the point past
 * which a shopkeeper gives up and types it in. Recalibrate from pilot upload timings, the way
 * `T-08` recalibrates credits.
 */
export const MIRA_MAX_IMAGES = 4;
/**
 * Decoded bytes per image, before base64. Decimal MB, so "roughly 1.5MB" is 1,500,000 and not
 * 1,572,864: the number is a budget for an upload, and the connection it is spent on is sold in
 * decimal too.
 *
 * Base64 costs a third on top, so one image is about 2,000,000 characters on the wire and a full
 * request about 8,000,000. Both sit inside the provider's own 12,000,000 and inside the API's
 * Fastify `bodyLimit`, and the second of those is held by a test rather than by reading: that limit
 * is set from `MAX_IMPORT_BYTES` for an unrelated feature, so nothing about Mira would notice it
 * being lowered.
 */
export const MIRA_MAX_IMAGE_BYTES = 1_500_000;
/**
 * What a client may send, which is one type, because there is one encode path.
 *
 * The provider accepts jpeg, png, gif and webp (Anthropic's vision page). This is narrower on
 * purpose: every image the product sends has been through a canvas re-encode, which is what applies
 * the crop below and what drops the EXIF a phone writes into a photograph, location included. That
 * re-encode emits JPEG, so JPEG is what exists. Listing a type nothing can produce would be a
 * branch no test could reach and a claim about a client that has not been written. A client that
 * genuinely needs PNG adds it here, where both sides read it.
 */
export const MIRA_IMAGE_MEDIA_TYPES = ['image/jpeg'];
/**
 * `AI-004`'s "stripped of identifying regions before transmission where possible", as a marker.
 *
 * > `AI-004` `MUST` No customer name, contact detail or identifier is sent to a model. Prescription
 * > images are stripped of identifying regions before transmission where possible, and the payload
 * > minimised to what the task needs.
 *
 * A photographed prescription slip has the patient's name written at the top of it. Neither payload
 * gate can see that: `assertPayloadMinimised` reads keys and `identifyingPayloadKeys` reads keys,
 * and a name in a photograph is not a key. "Where possible" is the appendix's own hedge, and the
 * implementation chosen for it is a crop the shopkeeper confirms: the panel shows the photograph,
 * they drag a box over the values, and only what is inside the box is encoded and uploaded.
 *
 * **This marker is an attestation and not proof.** It says the client took its user through that
 * step. A `curl` caller can set it, and no server-side check can tell a cropped photograph from an
 * uncropped one, because deciding that is the vision work being gated. What the marker buys is that
 * the step cannot be skipped by accident: `apps/counter` mints it in one function after a person
 * has chosen a region, the API refuses an image without it by name, and a contributor adding a
 * second upload path has to go looking for it.
 */
export const MIRA_CROP_CONFIRMED = 'cropped_by_staff';
/**
 * The payload keys that carry something other than text, read by `AI-003`'s gate **and** by the
 * provider that turns them into content blocks.
 *
 * One list, because the two have to agree: a provider reading a key the gate does not check would
 * send a photographed prescription past a residency setting that forbids it. That is the same drift
 * as `sph` against `sphere`, with a worse consequence.
 */
export const MIRA_IMAGES_KEY = 'images';
export const MIRA_PDF_KEY = 'pdf';
export const MIRA_ATTACHMENT_KEYS = [MIRA_IMAGES_KEY, MIRA_PDF_KEY];
const RX_LEGIBILITY = RX_READ_EYES.map((eye) => `${eye}.legibility`);
export const MIRA_CONTEXT_FIELDS = {
    rx_ocr: {
        paths: ['readable', 'script', 'writing', 'notes', ...RX_LEGIBILITY],
        why: 'The eight per-eye powers reach a lens and every one of them is a decision. These six say ' +
            'how well the slip could be read and in what hand and script, which is what a person needs ' +
            'in front of them while they judge the eight, and is written nowhere.',
    },
    catalogue_capture: {
        paths: ['readable', 'raw_text', 'notes'],
        why: 'Brand, model, the two codes, the three sizes, material and rim type become a frame record, ' +
            'so each is a decision. raw_text is every character group the model read and exists so a ' +
            'person can compare it with the proposal: it is the evidence, not a field.',
    },
    price_sheet_parse: {
        paths: ['unparsed_rows.*', 'warnings.*'],
        why: 'Every band, price, basis and surcharge is money and is a decision (AI-001 names a price ' +
            'explicitly). unparsed_rows is what the model could see and could not place, and warnings is ' +
            'what it wants looked at: both are there to be read before the rest is judged.',
    },
};
//# sourceMappingURL=mira-payloads.js.map